import { Fragment, Suspense, useMemo } from "react";
import { useLocation, useNavigate } from "react-router";
import { HelpCircle, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { AdminSpaceLink } from "@/features/auth/components/AdminSpaceLink";
import {
  CATALOGUE_CONFIG,
  CATALOGUE_IDS,
} from "@/features/boutique/constants/catalogue.constants";
import { ClassifiedAdsMenuBadge } from "@/features/classified-ads/components/ClassifiedAdsMenuBadge";
import { CLASSIFIED_ADS_MENU_KEY } from "@/features/classified-ads/configs/classifiedAdsPaths.config";
import { NavigationItemWithDrawer } from "@/features/navigation/components/NavigationItemWithDrawer";
import { SidebarCollapsibleItem } from "@/features/navigation/components/SidebarCollapsibleItem";
import { SidebarNavItem } from "@/features/navigation/components/SidebarNavItem";
import { SidebarScrollArea } from "@/features/navigation/components/SidebarScrollArea";
import {
  DRAWER_BEHAVIORS,
  WARNING_KEYS,
} from "@/features/navigation/config/menuBehaviors";
import type { NavigationChildData } from "@/features/navigation/types/navigation.type";
import { getActiveMenuKey } from "@/features/navigation/utils/activeMenuKey";
import { resolveIcon } from "@/features/navigation/utils/iconResolver";
import { reorderLocationsSlot } from "@/features/navigation/utils/reorderLocationsSlot";
import { useRentalCollections } from "@/features/rentals/hooks/useRentalCollections";
import { generateCollectionSlug } from "@/features/rentals/utils/collectionSlug.util";
import { useMenuItems } from "@/features/theme/hooks/useMenuItems";
import type { ApiMenuItem } from "@/features/theme/types/brandTheme.type";
import {
  getPendingFormRedirect,
  isMandatoryGuardExemptedPath,
  showMandatoryCustomFormToast,
} from "@/router/mandatoryGuard";
import { MandatoryFieldsToast } from "@/shared/components/MandatoryFieldsToast";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarFooter,
  SidebarSeparator,
  useSidebar,
} from "@/shared/components/ui/sidebar";
import { useAppSettings } from "@/shared/hooks/useAppSettings";
import { useUser } from "@/shared/hooks/useUser";
import { useUserMandatoryFieldsCheck } from "@/shared/hooks/useUserMandatoryFieldsCheck";
import { lazyWithRetry } from "@/shared/utils/lazyWithRetry";

const DynamicBlogDrawerContent = lazyWithRetry(() =>
  import("@/features/navigation/components/DynamicBlogDrawerContent").then(
    (m) => ({ default: m.DynamicBlogDrawerContent }),
  ),
);

const BoutiqueDrawerContent = lazyWithRetry(() =>
  import("@/features/boutique/components/BoutiqueDrawerContent").then(
    (m) => ({ default: m.BoutiqueDrawerContent }),
  ),
);

const DealsDrawerContent = lazyWithRetry(() =>
  import("@/features/deals/components/DealsDrawerContent").then(
    (m) => ({ default: m.DealsDrawerContent }),
  ),
);

const RentalsDrawerContent = lazyWithRetry(() =>
  import("@/features/rentals/components/RentalsDrawerContent").then(
    (m) => ({ default: m.RentalsDrawerContent }),
  ),
);

const ClassifiedAdsDrawerContent = lazyWithRetry(() =>
  import("@/features/classified-ads/components/ClassifiedAdsDrawerContent").then(
    (m) => ({ default: m.ClassifiedAdsDrawerContent }),
  ),
);

const DRAWER_COMPONENTS: Record<string, React.LazyExoticComponent<React.ComponentType<{
  baseRoute: string
  location: ReturnType<typeof useLocation>
}>>> = {
  blog: DynamicBlogDrawerContent,
  boutique_yeati: BoutiqueDrawerContent,
  bons_plans: DealsDrawerContent,
  locations: RentalsDrawerContent,
  [CLASSIFIED_ADS_MENU_KEY]: ClassifiedAdsDrawerContent,
};

type SidebarNavigationProps = {
  mobileSections?: React.ReactNode
  onMainNavigationClose?: () => void
  forceOpenBlogDrawer?: boolean
  forceOpenBoutiqueDrawer?: boolean
  forceOpenDealsDrawer?: boolean
};

const ICON_BY_COLLECTION_KIND: Record<string, string> = {
  hebergement: "Home",
  materiel: "Wrench",
  emplacement: "MapPin",
  vehicule: "Car",
  salle: "Building2",
  livre: "BookOpen",
};

const injectCatalogCollections = (
  items: ApiMenuItem[],
  collections: Array<{ id: string, name: string, kind: string | null }>,
  collectionsLoading: boolean,
): ApiMenuItem[] => items.flatMap((item): ApiMenuItem[] => {
  if (item.key === "locations") {
    // Pendant le chargement des collections, on masque entièrement le
    // bloc plutôt que d'afficher un item « Location » qui flashera et
    // sera remplacé par les collections une fraction de seconde plus
    // tard. Mieux vaut une sidebar légèrement vide qu'un saut visuel.
    if (collectionsLoading) {
      return [{
        ...item, label: "Location", route: "/catalogue", hidden: true,
      }];
    }
    if (collections.length === 0) {
      return [{ ...item, label: "Location", route: "/catalogue" }];
    }
    // Avec ≥1 collection, le parent reste hidden — il sert juste de
    // point de montage pour le drawer (auto-open sur `/catalogue*`).
    // Les enfants apparaissent seuls comme raccourcis collection.
    return [
      {
        ...item, label: "Location", route: "/catalogue", hidden: true,
      },
      ...collections.map((c) => ({
        key: `locations-collection-${c.id}`,
        label: c.name,
        icon: (c.kind && ICON_BY_COLLECTION_KIND[c.kind]) || "Package",
        route: `/catalogue?collection=${generateCollectionSlug(c.name)}`,
      })),
    ];
  }
  if (item.children?.length) {
    return [{ ...item, children: injectCatalogCollections(item.children, collections, collectionsLoading) }];
  }
  return [item];
});

function SidebarNavigation({
  mobileSections,
  onMainNavigationClose,
  forceOpenBlogDrawer,
  forceOpenBoutiqueDrawer,
  forceOpenDealsDrawer,
}: SidebarNavigationProps) {
  const rawMenuItems = useMenuItems();
  const { data: appSettings, isSuccess: appSettingsLoaded } = useAppSettings();
  const hasCatalog = appSettingsLoaded
    && !(appSettings?.hiddenFeatures.includes("service_catalog") ?? true);
  const { data: collectionsData, isLoading: collectionsLoading } = useRentalCollections({ enabled: hasCatalog });
  const menuItems = useMemo(
    () => reorderLocationsSlot(
      injectCatalogCollections(
        rawMenuItems,
        collectionsData?.collections ?? [],
        hasCatalog && collectionsLoading,
      ),
    ),
    [rawMenuItems, collectionsData?.collections, hasCatalog, collectionsLoading],
  );
  const location = useLocation();
  const activeMenuKey = useMemo(
    () => getActiveMenuKey(menuItems, location.pathname, location.search),
    [menuItems, location.pathname, location.search],
  );
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const {
    hasMandatoryMissingFields, mandatoryMissingFields, pendingMandatoryCustomForm, isLoading,
  } = useUserMandatoryFieldsCheck();
  const { linked_representative_id } = useUser();
  const {
    open: isSidebarOpen, openMobile, isMobile, state: sidebarState,
  } =
    useSidebar();
  const isDrawerSidebarOpen = isMobile ? openMobile : isSidebarOpen;
  const isIconMode = !isMobile && sidebarState === "collapsed";

  const productDetailMatch = location.pathname.match(
    /^\/boutique\/catalogue\/(\d+)\/product\/\d+$/,
  );
  const productCatalogueId = productDetailMatch
    ? Number(productDetailMatch[1])
    : null;

  const isCampingPage = location.pathname.includes("/camping");
  const isTicketingPage =
    location.pathname.includes("/billetterie") ||
    productCatalogueId === CATALOGUE_IDS.BILLETTERIE;
  const isCadeauxPage = location.pathname.includes("/cadeaux");
  const isCinemaPage = location.pathname.includes("/cinema");
  const isParcsLoisirsPage = location.pathname.includes("/parcs-loisirs");

  const handleSamePageNav = (
    event: React.MouseEvent<HTMLAnchorElement>,
    to: string,
  ) => {
    try {
      const target = new URL(to, window.location.origin);
      const isSame =
        target.pathname === location.pathname &&
        target.search === location.search;
      if (isSame) {
        event.preventDefault();
        event.stopPropagation();
        if (onMainNavigationClose) onMainNavigationClose();
        return;
      }
    }
    catch {
      // fallback noop
    }
  };

  const handleMandatoryGuardBeforeNav = (
    event: React.MouseEvent<HTMLAnchorElement>,
    to: string,
  ) => {
    try {
      const target = new URL(to, window.location.origin);
      if (isLoading) return false;
      const isExempted = isMandatoryGuardExemptedPath(target.pathname);
      if (!isExempted && hasMandatoryMissingFields) {
        event.preventDefault();
        event.stopPropagation();
        toast.custom(
          (t) => (
            <MandatoryFieldsToast
              toastId={t}
              missingFields={mandatoryMissingFields}
              onNavigateToProfile={() => navigate("/mon-profil?tab=profil")}
            />
          ),
          {
            id: "mandatory-fields-toast",
            duration: 8000,
          },
        );
        if (onMainNavigationClose) onMainNavigationClose();
        return true;
      }
      // Même règle que MandatoryFieldsGuard : sans ce blocage, le clic
      // naviguerait puis rebondirait en silence sur le formulaire.
      const pendingFormPath = hasMandatoryMissingFields
        ? null
        : getPendingFormRedirect(target.pathname, pendingMandatoryCustomForm);
      if (pendingFormPath && pendingMandatoryCustomForm) {
        event.preventDefault();
        event.stopPropagation();
        showMandatoryCustomFormToast({
          form: pendingMandatoryCustomForm,
          onNavigateToForm: () => navigate(pendingFormPath),
        });
        if (onMainNavigationClose) onMainNavigationClose();
        return true;
      }
    }
    catch {
      // noop
    }
    return false;
  };

  const handleDiscussionsNav = (to: string) => {
    try {
      const target = new URL(to, window.location.origin);
      if (target.pathname === "/discussions") {
        queryClient.invalidateQueries({ queryKey: ["discussions"] });
        queryClient.invalidateQueries({ queryKey: ["discussion-messages"] });
        queryClient.invalidateQueries({ queryKey: ["discussion-exchanges"] });
      }
    }
    catch {
      // noop
    }
  };

  const getCataloguePathById = (catalogueId: number | null): string | null => {
    if (catalogueId === null) return null;
    const config =
      CATALOGUE_CONFIG[catalogueId as keyof typeof CATALOGUE_CONFIG];
    return config?.path ?? null;
  };

  const handleBoutiqueBack = () => {
    const isCampingDetailPage = /^\/boutique\/camping\/\d+$/.test(
      location.pathname,
    );
    const isProductDetailPage = productCatalogueId !== null;

    if (isCampingPage) {
      if (isCampingDetailPage) {
        navigate(`/boutique/camping${location.search}`);
      }
      else {
        navigate("/boutique?drawer=boutique");
      }
    }
    else if (isTicketingPage) {
      if (
        isProductDetailPage &&
        productCatalogueId === CATALOGUE_IDS.BILLETTERIE
      ) {
        navigate("/boutique/billetterie?drawer=boutique");
      }
      else {
        navigate("/boutique?drawer=boutique");
      }
    }
    else if (isProductDetailPage) {
      const cataloguePath = getCataloguePathById(productCatalogueId);
      if (cataloguePath) {
        navigate(`${cataloguePath}?drawer=boutique`);
      }
      else {
        navigate("/boutique?drawer=boutique");
      }
    }
    else if (isCadeauxPage || isCinemaPage || isParcsLoisirsPage) {
      navigate("/boutique?drawer=boutique");
    }
    else {
      navigate("/");
      onMainNavigationClose?.();
    }
  };

  const handleDealsBack = () => {
    const isDealsDetailPage = /^\/bon-plans\/famille\/\d+$/.test(
      location.pathname,
    );
    const isDealsConfirmationPage =
      location.pathname === "/bon-plans/confirmation";

    if (isDealsDetailPage || isDealsConfirmationPage) {
      navigate("/bon-plans?drawer=deals");
    }
    else {
      navigate("/");
      onMainNavigationClose?.();
    }
  };

  const handleBlogBack = () => {
    const isArticleDetailPage = /^\/blog\/[^/]+$/.test(location.pathname);
    const hasFilters = location.search.includes("category=") || location.search.includes("period=");

    if (isArticleDetailPage) {
      navigate("/blog?drawer=blog");
    }
    else if (hasFilters) {
      navigate("/blog?drawer=blog");
    }
    else {
      navigate("/");
      onMainNavigationClose?.();
    }
  };

  const getBackHandler = (key: string): (() => void) | undefined => {
    const handlers: Record<string, () => void> = {
      blog: handleBlogBack,
      boutique_yeati: handleBoutiqueBack,
      bons_plans: handleDealsBack,
    };
    return handlers[key];
  };

  const getForceOpen = (key: string): boolean | undefined => {
    const forceOpenMap: Record<string, boolean | undefined> = {
      blog: forceOpenBlogDrawer,
      boutique_yeati: forceOpenBoutiqueDrawer,
      bons_plans: forceOpenDealsDrawer,
    };
    return forceOpenMap[key];
  };

  const renderLeafItem = (item: ApiMenuItem) => {
    const IconComponent = resolveIcon(item.icon, item.icon_type, item.icon_variant, item.icon_image);
    const route = item.route ?? "";
    const isActive = item.key === activeMenuKey;

    const drawerBehavior = DRAWER_BEHAVIORS[item.key];
    const DrawerContent = DRAWER_COMPONENTS[item.key];

    if (drawerBehavior && DrawerContent) {
      return (
        <NavigationItemWithDrawer
          key={item.key}
          to={route}
          label={item.label}
          icon={IconComponent}
          isActive={item.key === activeMenuKey}
          onMainNavigationClose={onMainNavigationClose}
          forceOpen={getForceOpen(item.key)}
          isSidebarOpen={isDrawerSidebarOpen}
          mayCloseOnClickOutside={false}
          closeOnLinkClick={drawerBehavior.closeOnLinkClick}
          drawerPersistenceKey={drawerBehavior.persistenceKey}
          onBack={getBackHandler(item.key)}
          hideBackButton={drawerBehavior.hideBackButton}
          hideTrigger={item.hidden ?? false}
          badge={item.key === CLASSIFIED_ADS_MENU_KEY ? <ClassifiedAdsMenuBadge /> : undefined}
        >
          <Suspense fallback={null}>
            <DrawerContent baseRoute={route} location={location} />
          </Suspense>
        </NavigationItemWithDrawer>
      );
    }

    if (!route) return null;

    const showWarning =
      WARNING_KEYS.has(item.key) && hasMandatoryMissingFields && !isLoading;

    return (
      <SidebarNavItem
        key={item.key}
        to={route}
        onClick={(e) => {
          const blocked = handleMandatoryGuardBeforeNav(e, route);
          if (!blocked) {
            handleSamePageNav(e, route);
            handleDiscussionsNav(route);
          }
          if (!e.defaultPrevented && onMainNavigationClose)
            onMainNavigationClose();
        }}
        isActive={isActive}
        icon={IconComponent}
        label={item.label}
        hasWarning={showWarning}
        warningIcon={AlertTriangle}
      />
    );
  };

  const renderNavItem = (item: ApiMenuItem) => {
    const children = item.children ?? [];
    const hasChildren = children.length > 0;
    const IconComponent = resolveIcon(item.icon, item.icon_type, item.icon_variant, item.icon_image);

    if (hasChildren && !item.route) {
      return null;
    }

    if (hasChildren && item.route) {
      if (isIconMode) {
        return (
          <Fragment key={item.key}>
            {children.map((child) => {
              const childRoute = child.route ?? "";
              const isChildActive = child.key === activeMenuKey;
              const showChildWarning =
                WARNING_KEYS.has(child.key) && hasMandatoryMissingFields && !isLoading;
              return (
                <SidebarNavItem
                  key={child.key}
                  to={childRoute}
                  onClick={(e) => {
                    const blocked = handleMandatoryGuardBeforeNav(e, childRoute);
                    if (!blocked) {
                      handleSamePageNav(e, childRoute);
                      handleDiscussionsNav(childRoute);
                    }
                    if (!e.defaultPrevented && onMainNavigationClose)
                      onMainNavigationClose();
                  }}
                  isActive={isChildActive}
                  icon={child.icon ? resolveIcon(child.icon, child.icon_type, child.icon_variant, child.icon_image) : IconComponent}
                  label={child.label}
                  hasWarning={showChildWarning}
                  warningIcon={AlertTriangle}
                />
              );
            })}
          </Fragment>
        );
      }

      const childrenData: NavigationChildData[] = children.map((child) => ({
        to: child.route ?? "",
        label: child.label,
        icon: child.icon ? resolveIcon(child.icon, child.icon_type, child.icon_variant, child.icon_image) : undefined,
      }));

      return (
        <SidebarCollapsibleItem
          key={item.key}
          item={{
            icon: IconComponent,
            label: item.label,
            children: childrenData,
          }}
          onMainNavigationClose={onMainNavigationClose}
        />
      );
    }

    return renderLeafItem(item);
  };

  const renderGroup = (item: ApiMenuItem) => {
    const children = item.children ?? [];
    const hasChildren = children.length > 0;

    if (hasChildren && !item.route) {
      return (
        <SidebarGroup key={item.key}>
          <SidebarGroupLabel>{item.label}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {children.map((child) => renderNavItem(child))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      );
    }

    return (
      <SidebarGroup key={item.key}>
        <SidebarGroupContent>
          <SidebarMenu>{renderNavItem(item)}</SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
    );
  };

  return (
    <>
      <SidebarScrollArea>
        {menuItems.map(renderGroup)}

        {isMobile && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarNavItem
                  to="/aide"
                  onClick={onMainNavigationClose}
                  isActive={location.pathname.startsWith("/aide")}
                  icon={HelpCircle}
                  label="Besoin d'aide ?"
                />
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarScrollArea>

      {!isMobile && (
        <SidebarFooter>
          <SidebarSeparator className="mx-0" />
          <SidebarMenu>
            <SidebarNavItem
              to="/aide"
              onClick={onMainNavigationClose}
              isActive={location.pathname.startsWith("/aide")}
              icon={HelpCircle}
              label="Besoin d'aide ?"
            />
            {linked_representative_id && (
              <AdminSpaceLink
                className={
                  isIconMode
                    ? "flex items-center justify-center rounded-md p-2 text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-primary [&>svg]:!mr-0 [&>span:not(:has(svg))]:hidden"
                    : "flex items-center px-2 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-primary rounded-md"
                }
              />
            )}
          </SidebarMenu>
        </SidebarFooter>
      )}

      {mobileSections && isMobile && (
        <div className="mt-auto p-2">{mobileSections}</div>
      )}
    </>
  );
}

SidebarNavigation.displayName = "SidebarNavigation";

export { SidebarNavigation };
